import {
  Dispatch,
  FC,
  FormEvent,
  HTMLAttributes,
  PropsWithChildren,
  SetStateAction,
} from 'react';
import { FormattedMessage } from 'react-intl';
import {
  EducationalStage,
  Gender,
  LocationOfCourse,
  PartOfTheDay,
} from '@les-sherpas/sherpas-toolbox';
import { Typography } from '@mui/material';

import FrequentQuestions from '@/components/Search/components/Filters/FrequentQuestions/FrequentQuestions';
import LevelFilter from '@/components/Search/components/Filters/LevelFilter/LevelFilter';
import LocationFilter from '@/components/Search/components/Filters/LocationFilter/LocationFilter';
import PriceFilter from '@/components/Search/components/Filters/PriceFilter/PriceFilter';
import { SidePanelFilters } from '@/components/Search/types';

import AvailabilityFilter from './AvailabilityFilter/AvailabilityFilter';
import GenderFilter from './GenderFilter/GenderFilter';
import OtherFilter from './OtherFilter/OtherFilter';
import messages from './messages';

import useStyles from './styles';

type Props = {
  availabilities: PartOfTheDay[];
  sidePanelFilters: SidePanelFilters;
  setSidePanelFilters: Dispatch<SetStateAction<SidePanelFilters>>;
} & HTMLAttributes<HTMLDivElement>;

const Filters: FC<PropsWithChildren<Props>> = ({
  sidePanelFilters,
  setSidePanelFilters,
  className,
  ...rest
}) => {
  const { classes, cx } = useStyles();

  return (
    <div className={classes.container}>
      <Typography variant="title2Xs" component="h6" gutterBottom>
        <FormattedMessage {...messages.title} />
      </Typography>
      <div className={cx(classes.filter, className)} {...rest}>
        <LocationFilter
          value={sidePanelFilters.courseLocation}
          handleChange={(event: FormEvent<HTMLInputElement>) => {
            const input = event.target as HTMLInputElement;
            const name = input.name as LocationOfCourse;
            setSidePanelFilters((prev) => ({
              ...prev,
              courseLocation: name,
            }));
          }}
        />

        <LevelFilter
          levels={sidePanelFilters.educationalStages}
          handleChange={(event: FormEvent<HTMLInputElement>) => {
            const input = event.target as HTMLInputElement;
            const name = input.name as EducationalStage;
            setSidePanelFilters((prev) => ({
              ...prev,
              educationalStages: input.checked
                ? [...prev.educationalStages, name]
                : prev.educationalStages.filter((item) => item !== name),
            }));
          }}
        />

        <PriceFilter
          sliderValue={[sidePanelFilters.prices[0], sidePanelFilters.prices[1]]}
          onSliderValueChange={(prices: [number, number]) => {
            setSidePanelFilters((prev) => ({
              ...prev,
              prices,
            }));
          }}
        />

        <AvailabilityFilter
          availabilities={sidePanelFilters.availabilities}
          handleChange={(event: FormEvent<HTMLInputElement>) => {
            const input = event.target as HTMLInputElement;
            const name = input.name as PartOfTheDay;
            setSidePanelFilters((prev) => ({
              ...prev,
              availabilities: input.checked
                ? [...prev.availabilities, name]
                : prev.availabilities.filter((item) => item !== name),
            }));
          }}
        />

        <GenderFilter
          value={sidePanelFilters.gender}
          handleChange={(event: FormEvent<HTMLInputElement>) => {
            const input = event.target as HTMLInputElement;
            const name = input.name as Gender;
            setSidePanelFilters((prev) => ({
              ...prev,
              gender: name,
            }));
          }}
        />

        <OtherFilter
          handleChange={(event: FormEvent<HTMLInputElement>) => {
            const input = event.target as HTMLInputElement;
            const name = input.name as Gender;
            setSidePanelFilters((prev) => ({
              ...prev,
              [name]: input.checked,
            }));
          }}
          isFastAnswering={sidePanelFilters.onlyAnswerFastSherpas}
          isCertified={sidePanelFilters.onlyCertifiedSherpas}
          isAvailable={sidePanelFilters.onlyAvailableSherpas}
        />
        <FrequentQuestions />
      </div>
    </div>
  );
};

export default Filters;
